/**
 * Carrousel d'images pour les variantes de bons cadeaux
 *
 * Affiche automatiquement toutes les images disponibles des variantes
 * avec rotation automatique et navigation manuelle (flèches + swipe mobile).
 *
 * Comportement :
 * - Rotation automatique toutes les 3.5 secondes
 * - Pause au survol (desktop) ou au touch (mobile)
 * - Navigation manuelle avec flèches
 * - Support swipe sur mobile
 * - Affichage du label de la variante en overlay
 *
 * Compatible avec les deux formats de données (nouveau format amount.axes et ancien format variantConfig.axes).
 *
 * @module VariantImageCarousel
 */

import {useState, useEffect, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Box, IconButton, Fade} from '@mui/material';
import {KeyboardArrowLeft, KeyboardArrowRight} from '@mui/icons-material';
import {useSwipeable} from 'react-swipeable';

/**
 * Convertit un chemin d'image en URL CDN complète
 *
 * @param {string} imagePath - Chemin de l'image (relatif ou absolu)
 * @return {string|null} URL complète de l'image ou null si pas d'image
 */
const getImageUrl = (imagePath) => {
  if (!imagePath) {
    return null;
  }
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const cdnEndpoint = process.env.REACT_APP_SPACES_CDN_ENDPOINT || 'https://files.brydge.app';
  return `${cdnEndpoint}/${imagePath}`;
};

/**
 * Carrousel d'images de variantes avec rotation automatique
 *
 * @param {Object} props - Props du composant
 * @param {Object} props.giftVoucherType - Type de bon cadeau complet
 * @param {number} [props.rotationInterval=3500] - Intervalle de rotation en ms
 * @return {JSX.Element|null} Carrousel ou null si pas d'images
 */
export function VariantImageCarousel({
  giftVoucherType,
  rotationInterval = 3500,
}) {
  const {t} = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showControls, setShowControls] = useState(false);

  /**
   * Récupère toutes les images de variantes disponibles
   *
   * Logique :
   * 1. Gère le nouveau format (amount.axes) et l'ancien format (variantConfig.axes)
   * 2. Cherche l'axe textuel principal avec images personnalisées
   * 3. Si trouvé : retourne toutes les images actives de cet axe
   * 4. Sinon : fallback sur l'image du type (si elle existe)
   */
  const variantImages = useMemo(() => {
    // Déterminer les axes à utiliser (nouveau format ou ancien format)
    const axes = giftVoucherType?.amount?.axes || giftVoucherType?.variantConfig?.axes;
    const typeImagePath = giftVoucherType?.display?.imageUrl || giftVoucherType?.imagePath;

    if (!axes) {
      // Type non-matriciel : utiliser l'image du type si elle existe
      const imageUrl = getImageUrl(typeImagePath);
      return imageUrl ? [{
        imagePath: imageUrl,
        label: giftVoucherType.name,
        isTypeImage: true,
      }] : [];
    }

    // Trouver les axes textuels avec images personnalisées de variantes
    const textAxesWithImages = axes
        .filter((axis) => axis.valueType === 'string')
        .filter((axis) => axis.values?.some((v) => v.variantImagePath))
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    if (textAxesWithImages.length === 0) {
      // Pas d'images de variantes : fallback sur image du type si elle existe
      const imageUrl = getImageUrl(typeImagePath);
      return imageUrl ? [{
        imagePath: imageUrl,
        label: giftVoucherType.name,
        isTypeImage: true,
      }] : [];
    }

    // Récupérer toutes les images de variantes de l'axe principal (filtrer les null)
    const primaryAxis = textAxesWithImages[0];
    const variantImagesList = primaryAxis.values
        .filter((v) => v.variantImagePath && v.isActive)
        .map((v) => ({
          imagePath: getImageUrl(v.variantImagePath),
          label: v.label,
          value: v.value,
          axisKey: primaryAxis.key,
          axisLabel: primaryAxis.label,
        }))
        .filter((v) => v.imagePath !== null)
        .sort((a, b) => {
          const originalA = primaryAxis.values.find((val) => val.value === a.value);
          const originalB = primaryAxis.values.find((val) => val.value === b.value);
          return (originalA?.displayOrder || 0) - (originalB?.displayOrder || 0);
        });

    // Ajouter l'image du type en première position si elle existe
    const typeImageUrl = getImageUrl(typeImagePath);
    if (typeImageUrl) {
      return [
        {
          imagePath: typeImageUrl,
          label: giftVoucherType.name,
          isTypeImage: true,
        },
        ...variantImagesList,
      ];
    }

    return variantImagesList;
  }, [giftVoucherType]);

  const maxSteps = variantImages.length;

  /**
   * Rotation automatique du carrousel
   *
   * Active uniquement si plus d'une image disponible et carrousel non en pause
   */
  useEffect(() => {
    if (maxSteps <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % maxSteps);
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [maxSteps, isPaused, rotationInterval]);

  const handleNext = useCallback(() => {
    setActiveStep((prev) => (prev + 1) % maxSteps);
  }, [maxSteps]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => (prev - 1 + maxSteps) % maxSteps);
  }, [maxSteps]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handleBack,
    preventDefaultTouchmoveEvent: true,
    trackMouse: false,
  });

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      handleBack();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      handleNext();
    }
  }, [handleBack, handleNext]);

  if (maxSteps === 0) return null;

  // Une seule image : affichage simple sans carrousel
  if (maxSteps === 1) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          backgroundImage: `url(${variantImages[0].imagePath})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
    );
  }

  return (
    <Box
      role="region"
      aria-roledescription={t('widgets.giftVoucher.carousel.ariaLabel', 'carrousel')}
      aria-label={t('widgets.giftVoucher.carousel.imageLabel', {label: variantImages[activeStep]?.label || '', defaultValue: `Carrousel d'images : ${variantImages[activeStep]?.label || ''}`})}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      sx={{
        'position': 'relative',
        'width': '100%',
        'height': '100%',
        'overflow': 'hidden',
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: '2px solid white',
          outlineOffset: '-4px',
        },
      }}
      onMouseEnter={() => {
        setIsPaused(true);
        setShowControls(true);
      }}
      onMouseLeave={() => {
        setIsPaused(false);
        setShowControls(false);
      }}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
      {...swipeHandlers}
    >
      {/* Container des images avec transition fluide */}
      <Box
        sx={{
          display: 'flex',
          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: `translateX(-${activeStep * 100}%)`,
          height: '100%',
        }}
      >
        {variantImages.map((variant, index) => (
          <Box
            key={`${variant.axisKey}-${variant.value}-${index}`}
            sx={{
              minWidth: '100%',
              width: '100%',
              height: '100%',
              flexShrink: 0,
              backgroundImage: `url(${variant.imagePath})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
            }}
          />
        ))}
      </Box>

      {/* Boutons de navigation (visible au hover ou touch) */}
      <Fade in={showControls || isPaused} timeout={300}>
        <Box>
          <IconButton
            onClick={handleBack}
            sx={{
              'position': 'absolute',
              'left': 8,
              'top': '50%',
              'transform': 'translateY(-50%)',
              'bgcolor': 'rgba(255, 255, 255, 0.9)',
              'boxShadow': 1,
              '&:hover': {
                bgcolor: 'white',
                boxShadow: 2,
              },
              'width': 40,
              'height': 40,
            }}
            aria-label={t('widgets.giftVoucher.carousel.previous', 'Image précédente')}
          >
            <KeyboardArrowLeft aria-hidden="true" />
          </IconButton>

          <IconButton
            onClick={handleNext}
            sx={{
              'position': 'absolute',
              'right': 8,
              'top': '50%',
              'transform': 'translateY(-50%)',
              'bgcolor': 'rgba(255, 255, 255, 0.9)',
              'boxShadow': 1,
              '&:hover': {
                bgcolor: 'white',
                boxShadow: 2,
              },
              'width': 40,
              'height': 40,
            }}
            aria-label={t('widgets.giftVoucher.carousel.next', 'Image suivante')}
          >
            <KeyboardArrowRight aria-hidden="true" />
          </IconButton>
        </Box>
      </Fade>

      {/* Indicateur du nombre d'images (coin supérieur droit) */}
      <Box
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          bgcolor: 'rgba(0, 0, 0, 0.6)',
          color: 'white',
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          fontSize: '0.75rem',
          fontWeight: 500,
          backdropFilter: 'blur(8px)',
        }}
      >
        <span aria-hidden="true">{activeStep + 1} / {maxSteps}</span>
        <span className="visually-hidden">
          {variantImages[activeStep]?.label || `Image ${activeStep + 1}`} sur {maxSteps}
        </span>
      </Box>
    </Box>
  );
}

VariantImageCarousel.propTypes = {
  giftVoucherType: PropTypes.shape({
    _id: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    imagePath: PropTypes.string,
    display: PropTypes.shape({
      imageUrl: PropTypes.string,
      isHighlighted: PropTypes.bool,
    }),
    amount: PropTypes.shape({
      type: PropTypes.string,
      axes: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        valueType: PropTypes.string,
        displayOrder: PropTypes.number,
        values: PropTypes.arrayOf(PropTypes.shape({
          value: PropTypes.any,
          label: PropTypes.string,
          variantImagePath: PropTypes.string,
          isActive: PropTypes.bool,
          displayOrder: PropTypes.number,
        })),
      })),
    }),
    variantConfig: PropTypes.shape({
      axes: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        valueType: PropTypes.string,
        displayOrder: PropTypes.number,
        values: PropTypes.arrayOf(PropTypes.shape({
          value: PropTypes.any,
          label: PropTypes.string,
          variantImagePath: PropTypes.string,
          isActive: PropTypes.bool,
          displayOrder: PropTypes.number,
        })),
      })),
    }),
  }).isRequired,
  rotationInterval: PropTypes.number,
};

export default VariantImageCarousel;
